import get from 'lodash/get'
import { formatNumber } from 'src/shared/helpers/formatNumber'

export const compose =
  (...validators: any) =>
  (value: any, allValues: any) =>
    validators.reduce((error: any, validator: any) => error || validator(value, allValues), undefined)

export const validateOnFixedValue =
  <FormFields extends object>(validator: any, field: keyof Partial<FormFields>, value: string | number) =>
  (fieldValue: FormFields[keyof FormFields], allValues?: Partial<FormFields>) => {
    if (String(get(allValues, field)).length === value) {
      return validator(fieldValue, allValues)
    }
  }

export const texted =
  (text = 'Обязательное поле') =>
  <Value>(value: Value) =>
    value ? undefined : text

export const required = texted()

export const requiredAddress = texted('Уточните адрес')

export const requiredCompany = texted('Заполните организацию')

export const trim = <Value>(value: Value) =>
  typeof value === 'string' && value.trim() === '' ? 'Обязательное поле' : undefined

export const trimRequired = compose(required, trim) as <Value>(value: Value) => string

export const min =
  (minValue: number, metric: undefined | string = '') =>
  (value: number) =>
    isNaN(value) || value >= minValue
      ? undefined
      : [`Значение должно быть больше чем ${minValue}`, metric].filter(Boolean).join(' ')

export const max =
  (maxValue: number, metric: undefined | string = '') =>
  (value: number) =>
    !isNaN(value) && value <= maxValue
      ? undefined
      : [`Значение должно быть меньше чем ${maxValue}`, metric].filter(Boolean).join(' ')

export const minMax =
  (
    minValue: number | undefined = 0,
    maxValue: number | undefined = 0,
    metric: undefined | string = '',
    formatFn: (value: number) => string | undefined = (value) => String(value)
  ) =>
  (value: number) => {
    if (!isNaN(value) && (value > maxValue || value < minValue)) {
      return [`Допустимо значение от\u00A0${formatFn(minValue)} до\u00A0${formatFn(maxValue)}`, metric]
        .filter(Boolean)
        .join('\u00A0')
    }
  }

function formatNumberByFloat(num: number) {
  const number = Number(num)
  if (Number.isInteger(number)) {
    return number.toString()
  }

  return number.toFixed(2).replace(/\.?0+$/, '')
}

function formatWeightByKg(kg: number) {
  if (kg < 1) {
    const grams = kg * 1000

    return `${grams}\u00A0гр`
  }
  if (kg < 1000) {
    const kilograms = kg

    return `${kilograms}\u00A0кг`
  }
  const tons = formatNumberByFloat(kg / 1000)

  return `${tons}\u00A0т`
}

function formatLengthByMetr(metr: number, postFix: string = '') {
  if (metr < 1) {
    return `${metr * 100}\u00A0см${postFix}`
  }

  return `${String(metr).replace('.', ',')}\u00A0м${postFix}`
}

type minMaxFormatMetrics = 'м³' | 'кг' | 'м' | '₽'

const FORMAT_FNS: Record<minMaxFormatMetrics, Function> = {
  'м³': (val: number) => `${String(val).replace('.', ',')}\u00A0м³`,
  кг: formatWeightByKg,
  м: formatLengthByMetr,
  '₽': (value: number) => `${formatNumber({ value, emptyOnZero: false, precision: 2 })}\u00A0₽`
}
export const minMaxFormat =
  (
    minValue: number | undefined = 0,
    maxValue: number | undefined = 0,
    {
      metric = 'кг',
      name = ''
    }: {
      metric: undefined | minMaxFormatMetrics
      name?: string
    }
  ) =>
  (value: number) => {
    const formatFn = FORMAT_FNS[metric]
    if (!isNaN(value) && (value > maxValue || value < minValue)) {
      if (name) {
        return [`${name} от\u00A0${formatFn(minValue)} до\u00A0${formatFn(maxValue)}`].filter(Boolean).join('\u00A0')
      }

      return [`Допустимо значение от\u00A0${formatFn(minValue)} до\u00A0${formatFn(maxValue)}`]
        .filter(Boolean)
        .join('\u00A0')
    }
  }

export const eqChar = (eqChar: number | number[]) => (value: number | string) => {
  const length = value ? value.toString().length : 0

  if (Array.isArray(eqChar)) {
    const isSomeValid = eqChar.some((minCharItem) => length === minCharItem)

    if (!isSomeValid) {
      return `Количество символов должно быть ${eqChar.join(' или ')}`
    }

    return
  }

  return length === eqChar ? undefined : `Количество символов должно быть ${eqChar}`
}

export const maxChar =
  (maxChar: number) =>
  <Value>(value: Value) => {
    if (!value) {
      return
    }

    return value.toString().length <= maxChar ? undefined : `Максимальное количество символов - ${maxChar}`
  }

export const email = <Value>(value: Value) =>
  typeof value === 'string' && value.includes('@') ? undefined : 'Неправильный адрес электронной почты'

export const alphabetSymbolsAllow = (value: string) =>
  /^[а-яА-ЯёЁ\s-]+$/.test(value) || !value ? undefined : 'Допустимые символы: буквы кириллицы, дефис и пробел'

export const phoneNumberValid = (value: string) =>
  /^7\d{10}$/.test(value) || !value ? undefined : 'Укажите номер телефона в международном формате +7 xxx xxx-xx-xx'

export const phoneMobileNumberValid = (value: string) =>
  /^79\d{9}$/.test(value) || !value ? undefined : 'Укажите номер телефона в международном формате +7 9xx xxx-xx-xx'
